//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { YAxis as RechartsYAxis, Label as RechartsLabel } from 'recharts';
import classnames from 'classnames';

import CustomizedAxisTick from './CustomizedAxisTick';
import { dashboardColors } from 'common/enums/ColorEnum';
import { type UnitType } from './ChartDataType';

import theme from './CustomYAxis.scss';

type Options = {
    rangeAxis: Object,
    data: Object,
    startIndex?: ?number,
    endIndex?: ?number,
    relativeHeight: number,
    relativeWidth: number,
    theme: Object,
    filteredDataKeys: Object,
    tickFormatter: number => ?(number | string),
    unit?: UnitType
};

export const createYAxis = ({
    rangeAxis,
    tickFormatter,
    data,
    startIndex,
    endIndex,
    relativeHeight,
    relativeWidth,
    filteredDataKeys,
    theme,
    unit
}: Options) => {
    const { values, refLines } = data;
    const totalTicks =
        typeof startIndex === 'number' && typeof endIndex === 'number'
            ? endIndex - startIndex + 1
            : data.values.length;
    const doubleWidth = relativeWidth >= 1.5;
    const doubleHeight = relativeHeight >= 1.5;
    const rangeObj = getAxisDomainRange(filteredDataKeys, values, refLines);

    return (
        <RechartsYAxis
            tickSize={4}
            domain={
                rangeObj.isRefLinesVisible
                    ? [rangeObj.min, rangeObj.max]
                    : undefined
            }
            width={relativeWidth >= 1.5 ? 85 : 70}
            stroke={dashboardColors.offWhite.code}
            dataKey={rangeAxis.key}
            tick={getTickCustomizer({
                totalTicks,
                tickFormatter,
                relativeHeight,
                relativeWidth,
                theme
            })}
        >
            <RechartsLabel
                content={
                    <ThemedAxisLabel
                        theme={theme}
                        doubleWidth={doubleWidth}
                        doubleHeight={doubleHeight}
                        label={rangeAxis.label}
                        unit={unit}
                    />
                }
            />
        </RechartsYAxis>
    );
};

// To get the maximum value of either data points or reference lines.
const getAxisDomainRange = (filteredDataKeys, values, refLines) => {
    let max = 0;
    let min = 0;
    let showRefLines = false;
    if (filteredDataKeys) {
        filteredDataKeys.map(dataKey => {
            values.map(value => {
                if (value[dataKey.key] > max) {
                    max = value[dataKey.key];
                }
                if (value[dataKey.key] < min) {
                    min = value[dataKey.key];
                }
            });
        });
    }
    if (refLines) {
        refLines.map(refLine => {
            if (refLine.alwaysShow) {
                max = max < Number(refLine.value) ? Number(refLine.value) : max;
                min = min > Number(refLine.value) ? Number(refLine.value) : min;
                showRefLines = true;
            }
        });
    }
    // Multiplying the max by 1.1 and Changing it to next hundred for avoiding ref line overlap.
    const rangeObj = {
        max: max * 1.1,
        min: Math.floor((min * 1.1) / 100) * 100,
        isRefLinesVisible: showRefLines
    };
    return rangeObj;
};

const renderCustomizedTick = (
    { totalTicks, tickFormatter, relativeHeight, relativeWidth, theme },
    props
) => {
    return (
        <CustomizedAxisTick
            totalTicks={totalTicks}
            useEllipsis={false}
            yAxis
            relativeHeight={relativeHeight}
            relativeWidth={relativeWidth}
            theme={theme}
            tickFormatter={tickFormatter}
            {...props}
        />
    );
};

const getTickCustomizer = tickOptions => {
    return renderCustomizedTick.bind(this, tickOptions);
};

class AxisLabel extends React.PureComponent<$FlowFixMeProps> {
    render() {
        const {
            viewBox,
            label,
            doubleWidth,
            doubleHeight,
            unit,
            theme
        } = this.props;
        const { height } = viewBox;
        const labelWidth = height + 10;
        const x = 18;
        const y = labelWidth;
        const transform = `translate(${x}, ${y}) rotate(-90, 0, 0)`;
        return (
            <svg xmlns="http://www.w3.org/2000/svg">
                <foreignObject
                    transform={transform}
                    width={labelWidth}
                    height={doubleWidth ? 36 : 18}
                >
                    <div
                        className={classnames(theme.axisLabel, {
                            [theme.doubleWidth]: doubleWidth,
                            [theme.doubleHeight]: doubleHeight
                        })}
                        xmlns="http://www.w3.org/1999/xhtml"
                    >
                        <span className={theme.labelText} title={label}>
                            {label} {unit ? `(${unit.label})` : ''}
                        </span>
                    </div>
                </foreignObject>
            </svg>
        );
    }
}
const ThemedAxisLabel = themr('AxisLabel', theme)(AxisLabel);
